import { Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import { enqueueSnackbar } from 'notistack'
import { VehicleToDoListParameters } from 'parameters/vehiclesParameters'
import Needs7501DocumentMichiganEmailExporterDialog from './Needs7501DocumentMichiganEmailExporterDialog'

export default function Needs7501DocumentMichiganToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isNeeds7501DocumentMichiganEmailExporterSaving, setNeeds7501DocumentMichiganEmailExporterSaving] =
    useState<boolean>(false)
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false })
  const [needs7501DocumentMichiganEmailExporterDialogOpen, setNeeds7501DocumentMichiganEmailExporterDialogOpen] =
    useState<boolean>(false)
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'createdDateTime',
    order: 'asc'
  })
  const [selectedVehicleGuid, setSelectedVehicleGuid] = useState<string | undefined>()
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getNeeds7501DocumentMichiganToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeeds7501DocumentMichiganToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs 7501 Document')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getNeeds7501DocumentMichiganToDoList(parameters)
  }, [getNeeds7501DocumentMichiganToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <Needs7501DocumentMichiganEmailExporterDialog
        initialValues={{}}
        isLoading={isNeeds7501DocumentMichiganEmailExporterSaving}
        onClose={() => {
          setNeeds7501DocumentMichiganEmailExporterDialogOpen(false)
        }}
        onSave={values => {
          setNeeds7501DocumentMichiganEmailExporterSaving(true)
          controllers.vehicles
            .updateNeeds7501DocumentMichigan(selectedVehicleGuid!, values)
            .then(() => {
              enqueueSnackbar('Needs 7501 Document Email Sent Successfully!', { variant: 'success' })
              setNeeds7501DocumentMichiganEmailExporterDialogOpen(false)
            })
            .finally(() => {
              setNeeds7501DocumentMichiganEmailExporterSaving(false)
            })
        }}
        open={needs7501DocumentMichiganEmailExporterDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created Date</MenuItem>

                    <MenuItem value='vehicleCheckInDateTime'>Vehicle Check-In Date</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='buyer'>Buyer</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporter', 'buyer', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'createdDateTime' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'vehicleCheckInDateTime' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (['createdDateTime', 'vehicleCheckInDateTime'].includes(filterField)
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      console.log('filterField', filterField)
                      console.log('filterValue', filterValue)

                      if (filterField === 'createdDateTime') {
                        setParameters({
                          ...parameters,
                          createdDateFrom: filterValueFrom,
                          createdDateTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleCheckInDateTime') {
                        setParameters({
                          ...parameters,
                          vehicleCheckInDateFrom: filterValueFrom,
                          vehicleCheckInDateTo: filterValueTo
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.createdDateFrom ||
                          parameters.createdDateTo ||
                          parameters.vehicleCheckInDateFrom ||
                          parameters.vehicleCheckInDateTo ||
                          parameters.exporter ||
                          parameters.buyer ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined,
                        vehicleCheckInDateFrom: undefined,
                        vehicleCheckInDateTo: undefined,
                        exporter: undefined,
                        buyer: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container spacing={1} alignItems='center'>
                {parameters.createdDateFrom && parameters.createdDateTo && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Created Date from "${moment(parameters.createdDateFrom).format('M/D/yyyy')}" to "${moment(
                        parameters.createdDateTo
                      ).format('M/D/yyyy')}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          createdDateFrom: undefined,
                          createdDateTo: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {parameters.vehicleCheckInDateFrom && parameters.vehicleCheckInDateTo && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Vehicle Check In Date from "${moment(parameters.vehicleCheckInDateFrom).format(
                          'M/D/yyyy'
                        )}" to "${moment(parameters.vehicleCheckInDateTo).format('M/D/yyyy')}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            vehicleCheckInDateFrom: undefined,
                            vehicleCheckInDateTo: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.exporter && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Exporter contains "${parameters.exporter}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            exporter: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.buyer && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Buyer contains "${parameters.buyer}"`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            buyer: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.vin && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`VIN contains ${parameters.vin}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            vin: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.year && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Year is ${parameters.year}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            year: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.make && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Make contains ${parameters.make}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            make: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {parameters.model && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Model contains ${parameters.model}`}
                        onDelete={() => {
                          setParameters({
                            ...parameters,
                            model: undefined
                          })
                        }}
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <ListBody>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    color='primary'
                                    checked={selectedVehicles.length === totalCount}
                                    indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                                    onChange={() => {
                                      if (selectedVehicles.length === totalCount) {
                                        setSelectedVehicles([])
                                      } else {
                                        setSelectedVehicles(vehicles ?? [])
                                      }
                                    }}
                                  />
                                </TableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='createdDateTime'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Created Date
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='vehicleCheckInDateTime'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Vehicle Check In
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='exporter'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Exporter
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='buyer'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Buyer
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='vin'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  VIN
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='year'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Year
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='make'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Make
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='model'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Model
                                </SortableTableCell>

                                <SortableTableCell
                                  onChangeSortByDirection={onChangeSortByDirection}
                                  propertyName='emailExporter'
                                  sortBy={parameters.orderBy}
                                  sortDirection={parameters.order}
                                >
                                  Email Exporter
                                </SortableTableCell>

                                <TableCell padding='checkbox'></TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {vehicles?.map((vehicle, index) => (
                                <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                  <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}>
                                    <Checkbox
                                      color='primary'
                                      checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                      onChange={() => {
                                        if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                          setSelectedVehicles(
                                            selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid)
                                          )
                                        } else {
                                          setSelectedVehicles([...selectedVehicles, vehicle])
                                        }
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell
                                    sx={theme => ({
                                      color:
                                        vehicle.createdDateTime &&
                                        moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4
                                          ? theme.palette.error.main
                                          : 'inherit',
                                      fontWeight:
                                        vehicle.createdDateTime &&
                                        moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4
                                          ? 'bold'
                                          : 'inherit'
                                    })}
                                  >
                                    {vehicle.createdDateTime ? formatDate(vehicle.createdDateTime) : '-'}
                                  </TableCell>

                                  <TableCell
                                    sx={theme => ({
                                      color:
                                        vehicle.vehicleCheckInDateTime &&
                                        moment().diff(moment(formatDate(vehicle.vehicleCheckInDateTime)), 'days') >= 4
                                          ? theme.palette.error.main
                                          : 'inherit',
                                      fontWeight:
                                        vehicle.vehicleCheckInDateTime &&
                                        moment().diff(moment(formatDate(vehicle.vehicleCheckInDateTime)), 'days') >= 4
                                          ? 'bold'
                                          : 'inherit'
                                    })}
                                  >
                                    {vehicle.vehicleCheckInDateTime ? formatDate(vehicle.vehicleCheckInDateTime) : '-'}
                                  </TableCell>

                                  <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                                  <TableCell>{vehicle.buyerCompany?.name}</TableCell>

                                  <TableCell>{vehicle.vin}</TableCell>

                                  <TableCell>{vehicle.year}</TableCell>

                                  <TableCell>{vehicle.make}</TableCell>

                                  <TableCell>{vehicle.model}</TableCell>

                                  <TableCell padding='checkbox' size='small'>
                                    <Button
                                      color='primary'
                                      onClick={() => {
                                        setSelectedVehicleGuid(vehicle.vehicleGuid)
                                        setNeeds7501DocumentMichiganEmailExporterDialogOpen(true)
                                      }}
                                      size='small'
                                      variant='text'
                                    >
                                      EMAIL
                                    </Button>
                                  </TableCell>

                                  <TableCell padding='checkbox' size='small'>
                                    <Button
                                      color='primary'
                                      endIcon={<Edit />}
                                      onClick={() => {
                                        navigate('/vehicles/' + vehicle.vehicleGuid + '/title')
                                      }}
                                      size='small'
                                      variant='text'
                                    >
                                      EDIT
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>

                            <TableFooter>
                              <TableRow>
                                <TablePagination
                                  rowsPerPageOptions={[5, 10, 25]}
                                  SelectProps={{ fullWidth: false }}
                                  count={totalCount}
                                  rowsPerPage={parameters.pageSize}
                                  page={parameters.page}
                                  onPageChange={(_, page) => {
                                    setParameters({
                                      ...parameters,
                                      page
                                    })
                                  }}
                                  onRowsPerPageChange={e => {
                                    setParameters({
                                      ...parameters,
                                      page: 0,
                                      pageSize: e.target.value as unknown as number
                                    })
                                  }}
                                />
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </TableContainer>
                      </Table>
                    </Box>
                  </ListBody>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Michigan Titles – Needs 7501 Document:**

Vehicles appear in the Michigan Titles – “Needs 7501 Document” to do list when Title Processing State = Michigan, the vehicle is associated with a PAPS in an Inbound Trip that has had its documents “Sent To Broker”, and a “7501 Document” is not associated with the vehicle.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:
   - Created,
   - Vehicle Check-In Date,
   - Exporter,
   - Buyer,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":
   - Created: Displays Filter Date From and Filter Date To.
   - Vehicle Check-In Date: Displays Filter Date From and Filter Date To.
   - Exporter: Displays a free-type filter field.
   - Buyer: Displays a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Created Column:**

The "Needs 7501 Document" To-Do List card's Created Date will be displayed in bold and red if the current date is greater than or equal to the Created Date plus 4 days.

**Vehicle Check-In Date Column:**

The "Needs 7501 Document" To-Do List card's Vehicle Check-In Date will be displayed in bold and red if the current date is greater than or equal to the Vehicle Check-In Date plus 1 day. The Vehicle
Check-In Date refers to the date when the first vehicle check-in image was associated with the vehicle.

**Email Exporter Action:**

Clicking the "EMAIL" text button will open the "Needs 7501 Document – Email Exporter" dialog.

**Edit Action:**

Clicking the "EDIT" button will navigate to the Vehicle Details – Titles Tab for that specific vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
